import { useState } from 'react'
import { PageHeader } from 'components/page-header/page-header'
import { addDays, timezoneFormatter, useTranslate, useWindowDimensions } from 'utils'
import { Loading, PageContentWrapper, PageError, VerticalContainer } from 'components/styled'
import { Alert, Tabs, Typography, theme } from 'antd'
import { RightOutlined } from '@ant-design/icons'
import { FormSection } from 'components/form-section/form-section'
import { AppointmentList } from 'components/appointment/appointment-list/appointment-list'
import { AppointmentFilter } from 'components/appointment/appointment-navigation/appointment-navigation'
import { useAnalytics, useConfig, useCurrentLocation, useMe, useNotification } from 'api'
import { AnalyticsStatsData, AnalyticsTopServicesData, Feature } from 'types'
import { useNavigate } from 'react-router-dom'
import { AnalyticsChartNavigation } from 'components/dashboard/analytics-chart-navigation/analytics-chart-navigation'
import { AnalyticsChart } from 'components/dashboard/analytics-chart/analytics-chart'
import { AnalyticsStats } from 'components/dashboard/analytics-stats/analytics-stats'
import { TopServicesChart } from 'components/dashboard/top-services-chart/top-services-chart'
const { Text } = Typography

const { Title } = Typography

export const Dashboard = () => {

    //hooks:
    const { token: {
        marginXXS,
        marginXS,
        margin,
        marginLG,
        paddingXS,
        paddingXXS,
        paddingSM,
        paddingLG,
        padding,
        fontSizeSM,
        colorBgContainer,
        colorTextDescription
    }} = theme.useToken()
    const { SM } = useWindowDimensions()
    const { __ } = useTranslate()
    const navigate = useNavigate()
    const { data: config } = useConfig()
    const { data: currentLocation } = useCurrentLocation()
    const timezone = currentLocation ? currentLocation.timezone : config ? config.settings.timezone : null

    const today = new Date()
    const [startDate, setStartDate] = useState(() => addDays(today, -30))
    const [endDate, setEndDate] = useState(today)
    
    //me:
    const { data: me, isLoading: isMeLoading } = useMe()
    
    //notifications:
    const { data: notification, isLoading: isNotificationLoading } = useNotification()

    const hasAnalyticsDashboard = config ? config.features.includes(Feature.ANALYTICS_DASHBOARD) : false

    const { data: analyticsData, isLoading: analyticsDataIsLoading } = useAnalytics(startDate, endDate, hasAnalyticsDashboard)

    const filteredData: AnalyticsStatsData[] = []
    let topServicesData: AnalyticsTopServicesData[] = []
    let totalAppointments: number = 0
    let totalSales: string = ''
    let currency = ''
    let currencySymbol = ''

    if (analyticsData) {
        analyticsData?.data?.forEach(dataValue => {
           const actualDateObj = new Date(dataValue.actual)
   
           if (actualDateObj >= startDate && actualDateObj <= endDate) {
               filteredData.push(dataValue)
               totalAppointments += dataValue?.value?.appointments || 0
           }
       })

       topServicesData = analyticsData?.topServices
       currency = analyticsData?.currency
       currencySymbol = analyticsData?.currencySymbol
       totalSales = analyticsData?.totalSales
    }

    return (!config || !timezone) ? null : isMeLoading ? <Loading/> : !me ? <PageError/> : (
        
        <PageContentWrapper>

            <VerticalContainer style={{ gap: margin }}>

                {/* header */}
                <div> {/* stops the header to be sticky on tablet */}
                    <PageHeader
                        title={
                            <Title
                                level={3}
                                style={{
                                    margin: 0,
                                    fontWeight: 'normal'
                                }}
                            >
                                {`${__`welcome`}, ${me.firstName ?? me.lastName ?? me.email}!`}
                            </Title>
                        }
                        description={__`check_whats_happening_in_your_sesami_world_your_service_commerce`}
                    />
                </div>

                <VerticalContainer style={{ padding: SM ? `0 ${padding}px` : 0, gap: marginLG }}>

                    {/* notification */}
                    {isNotificationLoading ? null :
                        notification === null ? null :
                    (notification === false || notification === undefined) ? (
                        <PageError
                            message={__`error_cannot_check_notifications`}
                            style={{ height: 'fit-content' }}
                        />
                    ) : notification.id && (
                        <div style={{ padding: SM ? 0 : `0 ${padding}px`}}>
                            <Alert
                                type={notification.type}
                                message={notification.message}
                                description={<div dangerouslySetInnerHTML={{ __html: notification.description }}/>}
                                showIcon
                                closable
                            />
                        </div>
                    )}

                    {hasAnalyticsDashboard && (
                    <FormSection
                        style={{
                            paddingTop: SM ? paddingSM : 0,
                            paddingBottom: SM ? paddingXXS : 0,
                            paddingLeft: SM ? paddingLG : padding,
                            paddingRight: SM ? paddingLG : padding,
                            flex: 1
                        }}
                        childrenWrapperStyle={{ padding: 0 }}
                    >
                        <div style={{
                            width: '100%',
                            paddingTop: SM ? paddingSM : padding,
                            paddingBottom: SM ? 0 : padding,
                            backgroundColor: colorBgContainer,
                        }}>
                            <AnalyticsChartNavigation
                                startDate={startDate}
                                endDate={endDate}
                                onStartDateChange={setStartDate}
                                onEndDateChange={setEndDate}
                            />
                        </div>
                        <Tabs
                            defaultActiveKey="analytics"
                            items={[
                                {
                                    key: 'analytics',
                                    label: __`analytics`,
                                    children: (
                                        <div>
                                            <div style={{
                                                paddingTop: SM ? paddingSM : padding,
                                                paddingBottom: SM ? padding : paddingXS,
                                                backgroundColor: colorBgContainer,
                                                minHeight: 85
                                            }}>
                                                {
                                                    analyticsDataIsLoading || !filteredData?.length ? <></> : (
                                                        <AnalyticsStats
                                                            totalAppointments={totalAppointments}
                                                            totalSales={totalSales}
                                                            currency={currency}
                                                        />
                                                    )
                                                }
                                            </div>
                                            <div style={{
                                                height: 400,
                                                width: '100%',
                                                paddingTop: SM ? paddingSM : padding,
                                                paddingBottom: SM ? padding : paddingXS,
                                                backgroundColor: colorBgContainer,
                                                borderRadius: 15
                                            }}>
                                                {
                                                    analyticsDataIsLoading ? <Loading /> : (
                                                        <AnalyticsChart data={filteredData} currencySymbol={currencySymbol} />
                                                    )
                                                }
                                            </div>
                                        </div>
                                    )
                                },
                                {
                                    key: 'top_services',
                                    label: __`top_services`,
                                    children: (
                                        <div style={{
                                            height: 400,
                                            width: '100%',
                                            paddingTop: SM ? paddingSM : padding,
                                            paddingBottom: SM ? padding : paddingXS,
                                            // paddingLeft: paddingXS,
                                            backgroundColor: colorBgContainer,
                                            borderRadius: 15
                                        }}>
                                            {
                                                analyticsDataIsLoading ? <Loading /> : (
                                                    <TopServicesChart data={topServicesData} />
                                                )
                                            }
                                        </div>
                                    )
                                }
                            ]}
                        />
                    </FormSection>
                    )}

                    {/* appointments list */}
                    <FormSection
                        title={(
                            <VerticalContainer style={{ gap: 0 }}>
                            
                                {/* time */}
                                <Title
                                    level={5}
                                    style={{
                                        paddingTop: SM ? 0 : paddingXXS,
                                        margin: `${marginXXS}px 0`
                                    }}
                                >
                                    {__`upcoming_appointments`}
                                </Title>
            
                                {/* timezone */}
                                <Text style={{ fontSize: fontSizeSM, color: colorTextDescription }}>
                                    {timezoneFormatter(timezone)}
                                </Text>
            
                            </VerticalContainer>
                        )}
                        topButton={{
                            label: __`view_all`,
                            icon: <RightOutlined style={{ fontSize: fontSizeSM }}/>,
                            style: {
                                padding: `${paddingXS}px ${paddingXXS}px `,
                                display: 'flex',
                                flexDirection: 'row-reverse',
                                alignItems: 'center',
                                gap: marginXS
                            },
                            onClick: () => navigate('/appointments')
                        }}
                        style={{ gap: margin }}
                        childrenWrapperStyle={{ padding: 0 }}
                    >
                    
                        <AppointmentList
                            resourceId={null}
                            search={null}
                            filter={AppointmentFilter.UPCOMING}
                            start={null}
                            end={null}
                        />

                    </FormSection>

                </VerticalContainer>

            </VerticalContainer>

        </PageContentWrapper>

    )

}
